'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Master from '@components/Layout/Master'
import { useLanguage } from '@contexts/LanguageContext'

const BENEFITS = [
  { icon: '🤝', title: 'makeAnImpact', desc: 'makeAnImpactDesc' },
  { icon: '🌱', title: 'learnAndGrow', desc: 'learnAndGrowDesc' },
  { icon: '👥', title: 'connect', desc: 'connectDesc' },
  { icon: '🏫', title: 'empower', desc: 'empowerDesc' },
] as const

const CATEGORIES = [
  { title: 'individualVolunteers', desc: 'individualVolunteersDesc' },
  { title: 'universityVolunteers', desc: 'universityVolunteersDesc' },
] as const

const ROLE_GROUPS = [
  { title: 'academicVolunteers', roles: ['roleTeacher', 'roleSpecialEd', 'roleCurriculum'] },
  {
    title: 'nonAcademicVolunteers',
    roles: ['roleFieldOps', 'roleDocumentation', 'roleCommunity'],
  },
] as const

// Stored values are stable English names so the back office reads the same in either language.
const CATEGORY_OPTIONS = [
  { value: 'Individual', label: 'categoryIndividual' },
  { value: 'University Partner', label: 'categoryUniversity' },
] as const

const ROLE_OPTIONS = [
  { value: 'Teacher (Numeracy / Literacy)', label: 'formRoleTeacher' },
  { value: 'Special Education Teacher', label: 'formRoleSpecialEd' },
  { value: 'Curriculum Support', label: 'formRoleCurriculum' },
  { value: 'Field Operations (Project Manager)', label: 'formRoleFieldOps' },
  { value: 'Documentation', label: 'formRoleDocumentation' },
  { value: 'Community Engagement Support', label: 'formRoleCommunity' },
] as const

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  category: '',
  roles: [] as string[],
  experience: '',
  motivation: '',
}

const VolunteerPage = () => {
  const router = useRouter()
  const { t } = useLanguage()

  const [formData, setFormData] = useState(EMPTY_FORM)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const toggleRole = (role: string) => {
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((item) => item !== role)
        : [...prev.roles, role],
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setStatus(null)

    try {
      const response = await fetch('/api/volunteer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, interests: formData.roles }),
      })

      if (response.ok) {
        setStatus({ type: 'success', message: t('thankYouVolunteer') })
        setFormData(EMPTY_FORM)
        setTimeout(() => router.push('/'), 3000)
      } else {
        setStatus({ type: 'error', message: t('somethingWentWrong') })
      }
    } catch {
      setStatus({ type: 'error', message: t('failedToSubmit') })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Master>
      <div className="volunteer-page">
        <section className="volunteer-hero">
          <div className="container">
            <h1>{t('volunteerHeroTitle')}</h1>
            <p>{t('volunteerHeroSubtitle')}</p>
          </div>
        </section>

        <section className="volunteer-content">
          <div className="container">
            <div className="volunteer-grid">
              <div className="volunteer-info">
                <h2>{t('whyVolunteer')}</h2>
                <div className="benefits">
                  {BENEFITS.map((benefit) => (
                    <div key={benefit.title} className="benefit-item">
                      <div className="benefit-icon">{benefit.icon}</div>
                      <h3>{t(benefit.title)}</h3>
                      <p>{t(benefit.desc)}</p>
                    </div>
                  ))}
                </div>

                <h2 className="volunteer-section-title">{t('volunteerCategories')}</h2>
                <div className="benefits">
                  {CATEGORIES.map((category, index) => (
                    <div key={category.title} className="benefit-item">
                      <h3>
                        {index + 1}. {t(category.title)}
                      </h3>
                      <p>{t(category.desc)}</p>
                    </div>
                  ))}
                </div>

                <h2 className="volunteer-section-title">{t('volunteerRoles')}</h2>
                <div className="benefits">
                  {ROLE_GROUPS.map((group, index) => (
                    <div key={group.title} className="benefit-item">
                      <h3>
                        {String.fromCharCode(65 + index)}. {t(group.title)}
                      </h3>
                      <ul className="volunteer-role-list">
                        {group.roles.map((role) => (
                          <li key={role}>{t(role)}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="volunteer-note">{t('allRolesOnField')}</p>
              </div>

              <div className="volunteer-form-wrapper">
                <h2>{t('volunteerApplication')}</h2>

                {status && <div className={`message ${status.type}`}>{status.message}</div>}

                <form onSubmit={handleSubmit} className="volunteer-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">
                        {t('firstName')} <span className="required">{t('required')}</span>
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="lastName">
                        {t('lastName')} <span className="required">{t('required')}</span>
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">
                        {t('email')} <span className="required">{t('required')}</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">
                        {t('phoneNumber')} <span className="required">{t('required')}</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      {t('volunteerCategory')} <span className="required">{t('required')}</span>
                    </label>
                    <div className="checkbox-group">
                      {CATEGORY_OPTIONS.map((option) => (
                        <label
                          key={option.value}
                          className={`checkbox-label ${formData.category === option.value ? 'checked' : ''}`}
                        >
                          <input
                            type="radio"
                            name="category"
                            value={option.value}
                            checked={formData.category === option.value}
                            onChange={handleInputChange}
                            required
                          />
                          <span>{t(option.label)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      {t('preferredRoles')} <span className="required">{t('required')}</span>
                    </label>
                    <small className="form-hint form-hint-neutral">{t('selectAtLeastOne')}</small>
                    <div className="checkbox-group">
                      {ROLE_OPTIONS.map((option) => {
                        const isChecked = formData.roles.includes(option.value)
                        return (
                          <label
                            key={option.value}
                            className={`checkbox-label ${isChecked ? 'checked' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleRole(option.value)}
                            />
                            <span>{t(option.label)}</span>
                          </label>
                        )
                      })}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="experience">{t('relevantExperience')}</label>
                    <textarea
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder={t('experiencePlaceholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="motivation">
                      {t('whyVolunteerQuestion')} <span className="required">{t('required')}</span>
                    </label>
                    <textarea
                      id="motivation"
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleInputChange}
                      rows={4}
                      required
                      placeholder={t('motivationPlaceholder')}
                    />
                  </div>

                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isSubmitting || formData.roles.length === 0}
                  >
                    {isSubmitting ? t('submitting') : t('submitApplication')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Master>
  )
}

export default VolunteerPage
